import type { ClinicalSummaryReview } from '@/pages/order/clinicalSummaryReviewTypes'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import { buildSummaryHighlightSpans } from './clinicalSummaryEditorHighlightSpans'

export const REVEAL_MARK_STAGGER_MS = 55
/** Last mark's start: a long note's thousands of marks share it instead of cascading for minutes. */
const REVEAL_MARK_SPREAD_MAX_MS = 1500
const REVEAL_MARK_FILL_MS = 500
export const REVEAL_ROW_FIRST_DELAY_MS = 200
/** One Figma `stagger` token covers marks and rows alike, so both steps are the same 55ms. */
export const REVEAL_ROW_STAGGER_MS = 55
const REVEAL_ROW_ENTER_MS = 320
export const REVEAL_POP_MS = 320

/** What the last extraction added, so only those marks, rows and chips play the reveal. */
export interface GenerationReveal {
  id: number
  codes: ReadonlySet<string>
  /** The counter starts at 0 when nothing was found before, else at the count minus the new ones. */
  countFromZero: boolean
  countMs: number
  durationMs: number
}

/** Delay between the reveal of one new mark and the next. */
export function revealMarkStaggerMs(newMarks: number): number {
  return newMarks > 1
    ? Math.min(REVEAL_MARK_STAGGER_MS, REVEAL_MARK_SPREAD_MAX_MS / (newMarks - 1))
    : REVEAL_MARK_STAGGER_MS
}

/** Count-up length: never under 900ms, so a fast answer still registers, and never over 2.2s. */
export function revealCountMs(newCount: number): number {
  return Math.max(900, Math.min(2200, newCount * REVEAL_MARK_STAGGER_MS + 400))
}

/** Ease-out cubic count between `from` and `to` after `elapsedMs` of `durationMs`. */
export function countUpValue(from: number, to: number, elapsedMs: number, durationMs: number): number {
  const progress = durationMs > 0 ? Math.min(1, Math.max(0, elapsedMs / durationMs)) : 1
  return Math.round(from + (to - from) * (1 - (1 - progress) ** 3))
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

function citedCodes(review: ClinicalSummaryReview | null | undefined): Set<string> {
  return new Set(
    Object.entries(review?.terms ?? {})
      .filter(([, term]) => term.reviewState !== 'removed' && term.citations.length > 0)
      .map(([code]) => code)
  )
}

export function buildGenerationReveal({
  id,
  summary,
  clinicalDiagnosis,
  review,
  previousReview,
  hadPhenotypes,
}: {
  id: number
  summary: string
  clinicalDiagnosis: CategorizedHPOOption[]
  review: ClinicalSummaryReview
  previousReview: ClinicalSummaryReview | null | undefined
  hadPhenotypes: boolean
}): GenerationReveal {
  const before = citedCodes(previousReview)
  const spans = buildSummaryHighlightSpans(summary, clinicalDiagnosis, review).filter((span) => span.tone !== 'removed')
  const cited = citedCodes(review)
  // A term whose phrases all sit under another term's mark has no span of its own, but it is still new.
  const codes = new Set(
    [
      ...spans.map((span) => span.code),
      ...clinicalDiagnosis.map((term) => term.id).filter((code) => cited.has(code)),
    ].filter((code) => !before.has(code))
  )
  const marks = spans.filter((span) => codes.has(span.code)).length
  const countMs = revealCountMs(codes.size)
  const durationMs = Math.max(
    marks > 0 ? (marks - 1) * revealMarkStaggerMs(marks) + REVEAL_MARK_FILL_MS : 0,
    codes.size > 0 ? REVEAL_ROW_FIRST_DELAY_MS + (codes.size - 1) * REVEAL_ROW_STAGGER_MS + REVEAL_ROW_ENTER_MS : 0,
    countMs + REVEAL_POP_MS
  )
  return { id, codes, countFromZero: !hadPhenotypes, countMs, durationMs }
}
